// VisitSelector.jsx

import React, { useState, useEffect, useRef } from "react";
import { FaCalendar, FaUserMd, FaHospital } from "react-icons/fa";
import api from "../../services/api";
import { formatDate } from "../../utils/helpers";

const VisitSelector = ({ patientId, onSelectVisit, selectedVisit }) => {

  const [visits, setVisits] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const dropdownRef = useRef(null);

  useEffect(() => {
    if (patientId) {
      loadPatientVisits();
    } else {
      setVisits([]);
    }
  }, [patientId]);

  useEffect(() => {

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);

  }, []);

  const loadPatientVisits = async () => {

    setIsLoading(true);

    try {

      const response = await api.getPatientVisits(patientId);

      setVisits(response.data || []);

    } catch (error) {

      console.error("Failed to load visits:", error);

    } finally {

      setIsLoading(false);

    }

  };

  return (
    <div className="space-y-2" ref={dropdownRef}>

      <label className="block text-sm font-medium text-gray-700">
        Visit (Optional)
      </label>

      {!selectedVisit ? (

        <div className="relative">

          <button
            type="button"
            onClick={() => setShowDropdown(!showDropdown)}
            disabled={!patientId || visits.length === 0}
            className="w-full px-4 py-2 text-left border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >

            {!patientId ? (
              <span className="text-gray-400">Select a patient first</span>
            ) : visits.length === 0 ? (
              <span className="text-gray-400">No visits found</span>
            ) : (
              <span className="text-gray-700">Select a visit...</span>
            )}

          </button>

          {showDropdown && visits.length > 0 && (

            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">

              {visits.map((visit) => (

                <div
                  key={visit.id}
                  className="p-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                  onClick={() => {
                    onSelectVisit(visit);
                    setShowDropdown(false);
                  }}
                >

                  <div className="flex items-center space-x-3">

                    <div className="bg-purple-100 p-2 rounded-full">
                      <FaHospital className="text-purple-600" />
                    </div>

                    <div className="flex-1">

                      <div className="font-medium text-gray-900">
                        {visit.visit_number}
                      </div>

                      <div className="flex items-center space-x-3 text-sm text-gray-500">

                        <span className="flex items-center">
                          <FaCalendar className="mr-1 h-3 w-3" />
                          {formatDate(visit.visit_date)}
                        </span>

                        <span className="flex items-center">
                          <FaUserMd className="mr-1 h-3 w-3" />
                          {visit.doctor?.full_name}
                        </span>

                      </div>

                      <div className="text-xs text-gray-400 mt-1">
                        {visit.visit_type?.name}
                      </div>

                    </div>

                  </div>

                </div>

              ))}

            </div>

          )}

        </div>

      ) : (

        <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">

          <div className="flex items-center justify-between">

            <div>

              <p className="font-medium text-purple-800">
                {selectedVisit.visit_number}
              </p>

              <p className="text-sm text-purple-600">
                {formatDate(selectedVisit.visit_date)} • Dr. {selectedVisit.doctor?.full_name}
              </p>

              <p className="text-xs text-purple-500 mt-1">
                {selectedVisit.visit_type?.name}
              </p>

            </div>

            <button
              onClick={() => onSelectVisit(null)}
              className="text-sm text-red-600 hover:text-red-800"
            >
              Clear
            </button>

          </div>

        </div>

      )}

    </div>
  );
};

export default VisitSelector;